import { styled, Typography, TypographyProps } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { merge } from 'lodash-es';
import { useAtomValue } from 'jotai';
import { CSSProperties, memo } from 'react';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import { companyStateLoadable } from '../../services/state/CompanyStateJ';
import { portcosByNameMapAtomJ } from '../../services/state/PortCosStateJ';
import { getCompanyProfileUrl } from '../../util/url-utils';
import { isGroupColumn, isTreeNodeRow } from '../AgTable/cell-renderers/GenericIdRenderer';
import { IMUIAvatarProps, MUIAvatar } from '../Avatar/MUIAvatar';

export interface ICompanyIdCellRendererParams<T> extends CustomCellRendererProps<T, number> {
  linkToProfile?: boolean;
  displayLogoInGroupRow?: boolean;
}

export function CompanyIdCellRenderer<T extends { companyId: number }>(
  params: ICompanyIdCellRendererParams<T>
) {
  const { node, data, value, linkToProfile = true } = params;
  const { companyId } = data ?? {};
  const portcosByName = useAtomValue(portcosByNameMapAtomJ);
  const companyL = useAtomValue(companyStateLoadable(companyId ?? value ?? -1));
  let company = companyL.state === 'hasData' ? companyL.data : undefined;
  const treeNodeRow = isTreeNodeRow(params);
  if (!company && !treeNodeRow) {
    // A tree node row's key is a path, never a company name.
    company = portcosByName.get(node.key ?? '') ?? null;
  }

  let href: string;
  let typographyProps;
  if (company && linkToProfile) {
    href = getCompanyProfileUrl(company.name, company.id);
    typographyProps = {
      component: 'a',
      color: 'secondary',
      href,
      target: '_blank',
      rel: 'noreferrer',
    } as TypographyProps;
  }

  if (!params.displayLogoInGroupRow && isGroupColumn(params) && node.group && !treeNodeRow) {
    return <>{node.key}</>;
  }
  if ((!isGroupColumn(params) && node.group) || !company) {
    return <></>;
  }
  return (
    <CompanyLogoAndName
      name={company.name}
      logoUrl={company.logoUrl ?? ''}
      typographyProps={typographyProps}
    />
  );
}

const CellContainer = styled('div')`
  align-items: center;
  column-gap: 0.75rem;
  display: grid;
  grid-template-columns: min-content 1fr;
  height: 100%;
`;

export interface ICompanyCellValue {
  name: string;
  logoUrl: string;
  role?: string;
  avatarProps?: Partial<IMUIAvatarProps>;
  typographyProps?: TypographyProps;
  style?: CSSProperties;
  url?: string;
  className?: string;
}

export const CompanyCellRenderer = memo(function CompanyCellRenderer(
  props: ICellRendererParams<unknown, ICompanyDataModel> &
    Pick<ICompanyIdCellRendererParams<ICompanyDataModel>, 'linkToProfile'>
) {
  const company = props.value;
  if (!company) {
    return null;
  }

  if (isGroupColumn(props)) {
    return <>{company.name}</>;
  }
  let href: string;
  let typographyProps;
  if (props.linkToProfile) {
    href = getCompanyProfileUrl(company.name, company.id);
    typographyProps = {
      component: 'a',
      color: 'secondary',
      href,
      target: '_blank',
      rel: 'noreferrer',
    } as TypographyProps;
  }

  const { name = '', logoUrl = '' } = company;

  return <CompanyLogoAndName name={name} logoUrl={logoUrl ?? ''} typographyProps={typographyProps} />;
});

export function CompanyLogoAndName({
  name,
  logoUrl,
  role,
  avatarProps,
  typographyProps = {},
  style,
  url,
  className,
}: ICompanyCellValue) {
  let mergedProps = typographyProps;
  if (url) {
    mergedProps = merge(
      {
        component: 'a',
        color: 'secondary',
        href: url,
        target: '_blank',
        rel: 'noreferrer',
      },
      { ...typographyProps }
    ) as TypographyProps;
  }
  return (
    <CellContainer role={role} style={style} className={className}>
      <MUIAvatar nameParts={name?.split(' ')} src={logoUrl} size={'medium'} {...avatarProps} />
      <Typography
        variant={'body2'}
        color={'text.primary'}
        style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
        {...mergedProps}
      >
        {name}
      </Typography>
    </CellContainer>
  );
}
